import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { UsePaginationReturn } from "@/hooks/usePagination";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

export interface TablePaginationProps {
  pagination: UsePaginationReturn;
  rowsPerPageLabel: string;
  pageOfLabel: string;
  onRefresh?: () => void | Promise<void>;
  isRefreshing?: boolean;
}

export function TablePagination({
  pagination,
  rowsPerPageLabel,
  pageOfLabel,
  onRefresh,
  isRefreshing = false,
}: TablePaginationProps): JSX.Element | null {
  const { t } = useTranslation();
  const {
    page,
    limit,
    total,
    totalPages,
    pageNumbers,
    hasNextPage,
    hasPrevPage,
    goToPage,
    setLimit,
    pageSizeOptions,
  } = pagination;

  if (total === 0) {
    return null;
  }

  const handleRefresh = (): void => {
    if (onRefresh) void onRefresh();
  };

  const pageIndexLabel = t("pages.common.pagination.pageOfPages", {
    current: page,
    total: totalPages,
  });

  return (
    <div className="flex flex-col gap-3 border-t border-border/50 bg-transparent px-3 pb-16 pt-3 sm:px-4 md:bg-[var(--fx-paper-2)]/40 md:pb-4 md:pt-4 lg:bg-[var(--fx-paper-2)]/60 dark:md:bg-[var(--dk-surface2)] dark:lg:bg-[var(--dk-surface2)]">
      {/* Mobile: two-row layout — nav on top, controls + range below */}
      <div className="flex flex-col gap-2 md:hidden">
        <div className="flex items-stretch gap-2 rounded-xl border border-border/80 bg-card px-2 py-2 shadow-sm">
          <PaginationPrevious
            className="h-10 w-10 shrink-0 transition-colors duration-150"
            onClick={() => hasPrevPage && goToPage(page - 1)}
            disabled={!hasPrevPage}
            aria-disabled={!hasPrevPage}
            aria-label={t("pages.common.pagination.previousPage")}
          />
          <span className="min-w-0 flex-1 px-1 text-center text-sm font-medium tabular-nums leading-tight text-foreground self-center">
            {pageIndexLabel}
          </span>
          <PaginationNext
            className="h-10 w-10 shrink-0 transition-colors duration-150"
            onClick={() => hasNextPage && goToPage(page + 1)}
            disabled={!hasNextPage}
            aria-disabled={!hasNextPage}
            aria-label={t("pages.common.pagination.nextPage")}
          />
        </div>
        <div className="flex items-center px-1">
          <div className="flex items-center gap-2">
            {onRefresh != null ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 transition-colors duration-150"
                onClick={handleRefresh}
                disabled={isRefreshing}
                aria-label={t("pages.common.pagination.refresh")}
                title={t("pages.common.pagination.refresh")}
              >
                <RefreshCw
                  className={isRefreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"}
                  aria-hidden
                />
              </Button>
            ) : null}
            <Label
              htmlFor="table-pagination-rows-mobile"
              className="sr-only"
            >
              {rowsPerPageLabel}
            </Label>
            <Select
              value={String(limit)}
              onValueChange={(v) => setLimit(Number(v))}
            >
              <SelectTrigger
                id="table-pagination-rows-mobile"
                className="h-9 w-[4.25rem] shrink-0 text-sm tabular-nums"
                aria-label={rowsPerPageLabel}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper">
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden flex-col gap-3 md:flex md:flex-row md:items-center md:justify-between md:gap-6">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-2">
          <div className="flex flex-wrap items-center gap-3">
            {onRefresh != null ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 transition-colors duration-150"
                onClick={handleRefresh}
                disabled={isRefreshing}
                aria-label={t("pages.common.pagination.refresh")}
                title={t("pages.common.pagination.refresh")}
              >
                <RefreshCw
                  className={isRefreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"}
                  aria-hidden
                />
              </Button>
            ) : null}
            <div className="flex min-w-0 items-center gap-2">
              <Label
                htmlFor="table-pagination-rows"
                className="shrink-0 whitespace-nowrap text-sm text-muted-foreground"
              >
                {rowsPerPageLabel}
              </Label>
              <Select
                value={String(limit)}
                onValueChange={(v) => setLimit(Number(v))}
              >
                <SelectTrigger
                  id="table-pagination-rows"
                  className="h-9 w-[70px] shrink-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-muted-foreground sm:whitespace-nowrap">
            {pageOfLabel}
          </p>
        </div>
        <div className="flex w-full min-w-0 shrink-0 justify-center overflow-x-auto pb-0.5 md:w-auto md:justify-end">
          <Pagination className="mx-auto w-auto">
            <PaginationContent className="flex flex-wrap justify-center gap-0.5">
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => hasPrevPage && goToPage(page - 1)}
                  disabled={!hasPrevPage}
                  aria-disabled={!hasPrevPage}
                  aria-label={t("pages.common.pagination.previousPage")}
                />
              </PaginationItem>
              {pageNumbers.map((p, idx) =>
                p === "ellipsis" ? (
                  <PaginationItem key={`ellipsis-${idx}`}>
                    <span className="flex h-9 w-9 items-center justify-center text-muted-foreground">
                      ...
                    </span>
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={page === p}
                      onClick={() => goToPage(p)}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() => hasNextPage && goToPage(page + 1)}
                  disabled={!hasNextPage}
                  aria-disabled={!hasNextPage}
                  aria-label={t("pages.common.pagination.nextPage")}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </div>
  );
}
